import type { ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { errorMessage } from '@/lib/api';

export interface ErrorStateProps {
  title?: ReactNode;
  /** A string, a node, or the thrown value itself — it gets translated. */
  message?: ReactNode;
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  /** Shown in small print so support can correlate with the audit trail. */
  requestId?: string;
  /** Heading level for the title. Use `h1` when this IS the page. */
  titleAs?: 'h1' | 'h2' | 'h3';
  size?: 'sm' | 'md';
  className?: string;
}

export function ErrorState({
  title = 'Não deu para carregar',
  message,
  error,
  onRetry,
  retryLabel = 'Tentar de novo',
  requestId,
  titleAs: Heading = 'h3',
  size = 'md',
  className,
}: ErrorStateProps) {
  const text = message ?? (error !== undefined ? errorMessage(error) : 'Algo saiu do traçado.');

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        size === 'md' ? 'px-gutter py-12' : 'px-4 py-6',
        className,
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-lg border border-loss/40 bg-loss-dim">
        <AlertTriangle aria-hidden="true" className="size-5 text-loss" />
      </div>

      <div className="max-w-prose">
        <Heading className={cn(Heading === 'h1' ? 'text-2xl' : 'text-lg', 'text-text')}>{title}</Heading>
        <p className="mt-1 text-base text-text-muted">{text}</p>
        {requestId ? (
          <p className="mt-2 text-2xs text-text-faint">
            Código do erro: <span className="num">{requestId}</span>
          </p>
        ) : null}
      </div>

      {onRetry ? (
        <Button variant="secondary" icon={RotateCcw} onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
